/**
 * 画面に収まる大きさで文字を出す段落(docs/02_game_design.md §3)。
 *
 * 1. `logic/textFit.ts` の見積もりでフォントサイズを決める(描画前に確定するのでチラつかない)
 * 2. 実際のフォント幅は見積もりとズレるので、描画後に高さを実測して必要なら数段階だけ縮める
 *
 * 実測ループは出題が変わったときだけ走る(1問につき最大6回のレイアウト参照)。
 */

import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { fitText, maxHeightPx, type FitRole, type Viewport } from '../../logic/textFit';

interface Props {
  readonly role: FitRole;
  /** サイズ見積もりに使う文字列(表示中に変化しない全文を渡すこと)。 */
  readonly measureText: string;
  readonly viewport: Viewport;
  readonly className?: string;
  readonly children: ReactNode;
  readonly ariaLabel?: string;
}

/** 実測で縮めるときの倍率と最大回数。0.9^5 ≒ 0.59 まで縮む。 */
const SHRINK_RATIO = 0.9;
const SHRINK_STEPS = 5;

export function FitText({ role, measureText, viewport, className, children, ariaLabel }: Props) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const fit = useMemo(() => fitText(role, measureText, viewport), [role, measureText, viewport]);
  const maxHeight = maxHeightPx(role, viewport);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    let size = fit.fontSize;
    element.style.fontSize = `${size}px`;
    for (let step = 0; step < SHRINK_STEPS; step += 1) {
      // jsdom など実測できない環境では 0 が返るので、そのまま抜ける。
      if (element.scrollHeight === 0) return;
      const overflows =
        element.scrollHeight > maxHeight + 1 || element.scrollWidth > element.clientWidth + 1;
      if (!overflows) return;
      size = Math.max(fit.minFontSize, Math.round(size * SHRINK_RATIO));
      element.style.fontSize = `${size}px`;
      if (size === fit.minFontSize) return;
    }
    // 依存に children を入れないこと。ローマ字ガイドは1打鍵ごとに children が変わるので、
    // 入れると毎フレーム強制レイアウトが走って入力遅延50ms要件(CLAUDE.md ルール6)を壊す。
    // 実測が必要なのは「出題が変わった = measureText が変わった」ときだけ。
  }, [fit, maxHeight]);

  return (
    <p
      ref={ref}
      className={className}
      aria-label={ariaLabel}
      // max-height は付けない。付けると影や下に出る字(g/y)が切れる。
      // 高さの上限は下の縮小ループが見張り、最後の砦は .word-panel の overflow:hidden。
      style={{ fontSize: `${fit.fontSize}px` }}
    >
      {children}
    </p>
  );
}
